import React, { useState } from 'react';
import { Button, Spinner, Card, ProgressBar, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import PageContainer from '../../layout/PageContainer';

// Import all initial data required for the full system reset from their individual files
import { users as initialUsers } from '../../../data/mockUsers';
import { customers as initialCustomers } from '../../../data/mockCustomers';
import { watchlists as initialWatchlists } from '../../../data/mockWatchlists';
import { rules as initialRules } from '../../../data/mockRules';

const SystemSimulator = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState('');
    const { user } = useAuth();

    // --- WORKFLOW 1: FULL DEMO MODE RESET ---
    const handleFullReset = async () => {
        if (!window.confirm("This will clear all existing transactional data (customers, cases, alerts, transactions, etc.) and generate a new, large dataset for the KSA/UAE region. Are you sure you want to proceed?")) {
            return;
        }
        setIsLoading(true);

        // Step 1: Clear existing data
        setMessage('Step 1/4: Clearing existing database...');
        setProgress(10);
        const storesToClear = ['customers', 'transactions', 'alerts', 'cases', 'users', 'watchlists', 'rules'];
        for (const store of storesToClear) {
            const allItems = await dbService.getAll(store);
            if (allItems.length > 0) {
                await Promise.all(allItems.map(item => dbService.delete(store, item.id)));
            }
        }

        // Step 2: Populate with base profiles, lists, and rules
        setMessage('Step 2/4: Populating 30 KYC profiles, users, and watchlists...');
        setProgress(25);
        await Promise.all([
            ...initialUsers.map(u => dbService.add('users', u)),
            ...initialCustomers.map(c => dbService.add('customers', c)),
            ...initialWatchlists.map(wl => dbService.add('watchlists', wl)),
            ...initialRules.map(r => dbService.add('rules', r)),
        ]);
        
        // Step 3: Simulate a large volume of historical transactions
        setMessage('Step 3/4: Simulating 500 historical KSA/UAE transactions...');
        setProgress(50);
        // We pass the initial customers directly to the generator for this first run
        const historicalTransactions = generateTransactions(500, initialCustomers, ['IRN', 'SYR', 'YEM']);
        await Promise.all(historicalTransactions.map(txn => dbService.add('transactions', txn)));
        
        // Step 4: Generate pre-labeled suspicious alerts for demonstration
        setMessage('Step 4/4: Generating pre-labeled suspicious alerts...');
        setProgress(80);
        const structuringCustomer = initialCustomers.find(c => c.id === 'CUST-101'); // Khaled Al-Fahd
        if (structuringCustomer) {
            for (let i = 0; i < 5; i++) {
                const alert = {
                    ruleName: 'Potential Structuring (Demo)', customerId: structuringCustomer.id,
                    customerName: structuringCustomer.fullName, status: 'New', riskScore: 75,
                    date: new Date().toISOString(), transactions: [],
                };
                await dbService.add('alerts', alert);
            }
        }

        setMessage('Activation Complete!');
        setProgress(100);
        toast.success(`Demo Mode Activated: Populated the system with a large, realistic KSA/UAE dataset.`);
        await dbService.addLog({ user: user.name, action: 'Activated Demo Mode', details: 'Performed a full data reset and generation.' });
        setIsLoading(false);
    };
    
    // --- WORKFLOW 2: REAL-TIME SIMULATION ---
    const handleRealTimeSim = async () => {
        setIsLoading(true);
        setMessage('Simulating new real-time activity...');
        setProgress(0);
        
        const [productionRules, customers, highRiskCountriesData, mlModel] = await Promise.all([
            dbService.getAll('rules').then(allRules => allRules.filter(r => r.environment === 'Production')),
            dbService.getAll('customers'),
            dbService.getById('masters', 'highRiskCountries'),
            dbService.getById('ml_models', 'txn-risk-model'),
        ]);

        if (customers.length === 0) {
            toast.error("No customers found. Please activate Demo Mode first.");
            setIsLoading(false);
            return;
        }

        // Correctly extract the .data array from the database object
        const highRiskCountriesList = highRiskCountriesData ? highRiskCountriesData.data : [];

        const newTransactions = generateTransactions(20, customers, highRiskCountriesList);
        setProgress(30);

        const alertsCount = await runAlertEngineAndUpdateRisk(newTransactions, productionRules, highRiskCountriesList, mlModel);
        
        await Promise.all(newTransactions.map(txn => dbService.add('transactions', txn)));
        setProgress(90);

        setMessage('Simulation Complete!');
        setProgress(100);
        await dbService.addLog({ user: user.name, action: 'Simulated Real-Time Activity', details: `Created ${newTransactions.length} new transactions, resulting in ${alertsCount} alerts.` });
        toast.success(`Simulated ${newTransactions.length} new transactions. ${alertsCount} new alerts were generated.`);
        setIsLoading(false);
    };

    // --- HELPER FUNCTIONS ---
    const generateTransactions = (count, customers, highRiskCountriesList) => {
        const transactions = [];
        if (!customers || customers.length === 0) return [];

        // --- LOCALIZED DATA ---
        const KSA_UAE_COUNTERPARTIES = [
            'Emaar Properties PJSC', 'Al Futtaim Group', 'Jeddah Automotive Imports', 'Riyadh Consulting FZ',
            'Abu Dhabi Future Energy Co.', 'Saudi Aramco Supplier', 'Dubai Properties LLC', 'Almarai Company'
        ];
        const REGIONAL_COUNTRIES = ['EGY', 'JOR', 'BHR', 'KWT', 'OMN'];
        // --- END LOCALIZED DATA ---

        for (let i = 0; i < count; i++) {
            const randomCustomer = customers[Math.floor(Math.random() * customers.length)];
            
            let counterpartyCountry;
            const flowChance = Math.random();
            if (flowChance > 0.9 && highRiskCountriesList.length > 0) {
                counterpartyCountry = highRiskCountriesList[Math.floor(Math.random() * highRiskCountriesList.length)];
            } else if (flowChance > 0.7) {
                counterpartyCountry = REGIONAL_COUNTRIES[Math.floor(Math.random() * REGIONAL_COUNTRIES.length)];
            } else {
                counterpartyCountry = Math.random() > 0.5 ? 'SAU' : 'ARE';
            }

            const transaction = {
                date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                customerName: randomCustomer.fullName,
                customerId: randomCustomer.id,
                amount: Math.floor(Math.random() * 150000) + 30, // Min 30, up to ~150k SAR/AED
                type: Math.random() > 0.5 ? 'Cash Deposit' : 'Wire Transfer',
                counterpartyCountry: counterpartyCountry,
                counterpartyName: KSA_UAE_COUNTERPARTIES[Math.floor(Math.random() * KSA_UAE_COUNTERPARTIES.length)],
            };
            transactions.push(transaction);
        }
        return transactions;
    };

    const runAlertEngineAndUpdateRisk = async (transactions, rules, highRiskCountriesList, mlModel) => {
        let alertsCreated = 0;
        for (const txn of transactions) {
            const customer = await dbService.getById('customers', txn.customerId);
            if (!customer || !customer.riskBreakdown) {
                console.warn(`Skipping transaction for customer ${txn.customerId} - data structure is invalid.`);
                continue;
            }

            let customerTxnRiskScoreIncrease = 0;
            const riskEvents = [];

            // 1. Event-Driven Risk Update
            if (highRiskCountriesList.includes(txn.counterpartyCountry)) {
                const impact = 5;
                customerTxnRiskScoreIncrease += impact;
                riskEvents.push({ date: txn.date, description: `Transaction to High-Risk Country (${txn.counterpartyCountry})`, impact });
            }
            if (txn.amount > 37500) {
                const impact = 5;
                customerTxnRiskScoreIncrease += impact;
                riskEvents.push({ date: txn.date, description: `High-Value Transaction (${txn.amount.toLocaleString()} SAR/AED)`, impact });
            }

            // 2. Rule-Based Alert Generation
            for (const rule of rules) {
                let triggered = false;
                const { logic } = rule;
                if (logic.type === 'transaction') {
                    if (logic.operator === '>' && txn.type === logic.txnType && txn.amount > logic.value) triggered = true;
                    if (logic.field === 'counterpartyCountry' && highRiskCountriesList.includes(txn.counterpartyCountry)) triggered = true;
                }
                if (logic.type === 'kyc') {
                    if (logic.operator === '===' && customer.customFields[logic.field] === logic.value) triggered = true;
                }

                if (triggered) {
                    alertsCreated++;
                    const mlScore = mlModel ? mlModel.baseScoreAddition : 0;
                    const finalAlertScore = Math.round(rule.riskScore + mlScore + (txn.amount / 5000));
                    const impact = Math.round(rule.riskScore / 10);
                    customerTxnRiskScoreIncrease += impact;
                    riskEvents.push({ date: txn.date, description: `Rule Trigger: ${rule.name}`, impact });
                    const alertData = {
                        ruleName: rule.name, customerId: txn.customerId, customerName: txn.customerName,
                        status: 'New', date: new Date().toISOString(), transactions: [txn], riskScore: finalAlertScore,
                    };
                    if (rule.autoCreateCase) {
                        const newCase = {
                            customerId: txn.customerId, customerName: txn.customerName, risk: 'High',
                            status: rule.autoEscalate ? 'Escalated' : 'Open',
                            assignedTo: rule.autoAssignTo || 'Unassigned',
                            openDate: new Date().toISOString().split('T')[0],
                            notes: [{ user: 'System', text: `Case auto-created from critical rule: ${rule.name}`, timestamp: new Date().toISOString() }],
                            auditTrail: [{ user: 'System', action: `Case Auto-Created from Rule: ${rule.name}`, timestamp: new Date().toISOString() }],
                            transactions: [txn]
                        };
                        const newCaseId = await dbService.add('cases', newCase);
                        alertData.status = `Case Created (${newCaseId})`;
                        const toastMsg = rule.autoEscalate 
                            ? `Critical Alert: Case #${newCaseId} was auto-created and escalated!`
                            : `Critical Alert: Case #${newCaseId} was auto-created.`;
                        toast.info(toastMsg);
                    }
                    await dbService.add('alerts', alertData);
                }
            }
            
            // 3. Finalize and Persist Reassessment
            if (customerTxnRiskScoreIncrease > 0) {
                const updatedCustomer = { ...customer };
                updatedCustomer.riskBreakdown.txnScore += customerTxnRiskScoreIncrease;
                updatedCustomer.riskBreakdown.total = updatedCustomer.riskBreakdown.kycScore + updatedCustomer.riskBreakdown.txnScore;
                updatedCustomer.riskBreakdown.events.push(...riskEvents);
                await dbService.update('customers', updatedCustomer);
            }
        }
        return alertsCreated;
    };

    return (
        <PageContainer title="System & Data Simulator">
            <p>This is the central command center for managing the application's data. Use this module to populate the system with a large dataset for demonstration, and then simulate new, real-time activity to see the system react.</p>
            <Row>
                <Col md={6} className="d-flex">
                    <Card className="mb-4 w-100">
                        <Card.Header as="h5">Step 1: Full System Reset (KSA/UAE Data)</Card.Header>
                        <Card.Body className="d-flex flex-column text-center">
                            <Card.Text className="flex-grow-1">
                                Click this button to perform a <strong>one-time setup</strong>. It clears all existing data and populates the system with a rich, localized dataset for the KSA & UAE region. <strong>Use this first.</strong>
                            </Card.Text>
                            <Button onClick={handleFullReset} disabled={isLoading} size="lg" variant="success">
                                {isLoading ? <Spinner as="span" size="sm"/> : 'Activate KSA/UAE Demo Mode'}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6} className="d-flex">
                    <Card className="mb-4 w-100">
                        <Card.Header as="h5">Step 2: Real-Time Simulation</Card.Header>
                        <Card.Body className="d-flex flex-column text-center">
                            <Card.Text className="flex-grow-1">
                                After activating demo mode, click this to simulate a "new day" of activity. It adds <strong>20 new transactions</strong> and runs them through the live rule engine and AI model.
                            </Card.Text>
                            <Button onClick={handleRealTimeSim} disabled={isLoading} size="lg" variant="primary">
                                {isLoading ? <Spinner as="span" size="sm"/> : 'Simulate 20 New Transactions'}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            {isLoading && (
                <div className="mt-3">
                    <ProgressBar animated now={progress} label={`${progress.toFixed(0)}%`} />
                    <p className="text-center mt-2"><strong>{message}</strong></p>
                </div>
            )}
        </PageContainer>
    );
};

export default SystemSimulator;